/**
 * User Helper Utilities
 * 
 * Reusable functions for working with user data
 */

import { IUser } from '../types/auth.types';

/**
 * Get user's full name
 * Falls back to username if firstName/lastName not available
 */
export function getUserFullName(user: IUser): string {
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return fullName || user.username;
}

/**
 * Get user's display name (first name or username)
 * Useful for greetings like "Welcome, John!"
 */
export function getUserDisplayName(user: IUser): string {
  return user.firstName || user.username;
}

/**
 * Get user's initials for avatar
 * Returns 2 letters: First + Last name initials, or first 2 letters of username
 */
export function getUserInitials(user: IUser): string {
  const first = user.firstName?.[0] || '';
  const last = user.lastName?.[0] || '';
  
  if (first && last) {
    return `${first}${last}`.toUpperCase();
  }
  
  if (first) {
    return first.toUpperCase();
  }
  
  return user.username.substring(0, 2).toUpperCase();
}

/**
 * Get user's primary role
 * Returns the first role or 'Member' as default
 */
export function getUserRole(user: IUser): string {
  if (!user.roles || user.roles.length === 0) {
    return 'Member';
  }
  
  // Capitalize first letter
  const role = user.roles[0];
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/**
 * Format user for display (name + role)
 * Example: "John Doe (Seller)"
 */
export function formatUserDisplay(user: IUser, includeRole = false): string {
  const name = getUserFullName(user);
  if (includeRole) {
    const role = getUserRole(user);
    return `${name} (${role})`;
  }
  return name;
}

